import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./style.less";

import { Alert, Button, Col, DatePicker, Row, Skeleton } from "antd";
import { CaretUpOutlined, CaretDownOutlined } from "@ant-design/icons";
import SalesChart from "./SalesChart";
import PendingOrders from "./PendingOrders";
import Products from "./Products";
import axios from "axios";
import { ClockIcon, PackageCheck, ShoppingBag, Truck } from "lucide-react";
import PPPageHeader from "../../components/pageHeader/PPPageHeader";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";

dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const { RangePicker } = DatePicker;

const DATE_FORMAT = "YYYY/MM/DD";

/** Статистик карт бүрийн дүрс ба өнгө. Түлхүүр нь backend-ийн `title`. */
const STATS_STYLE = {
  Нийт: { color: "#2ABB7F", Icon: ShoppingBag },
  Хүргэгдсэн: { color: "#388BFF", Icon: PackageCheck },
  "Хүргэлтэд гарсан": { color: "#F18D13", Icon: Truck },
  "Хүргэхэд бэлэн": { color: "#EF5C48", Icon: ClockIcon },
};

/** Backend шинэ төрөл нэмбэл карт алга болохгүй, саарал дүрстэй гарна */
const FALLBACK_STATS_STYLE = { color: "#758195", Icon: ShoppingBag };

const GROWTH_UP_COLOR = "#2ABB7F";
const GROWTH_DOWN_COLOR = "#FA7268";

/** Дүрсний дэвсгэрийг үндсэн өнгөнөөс нь гаргана ("#2ABB7F" → rgba) */
const withAlpha = (hex, alpha) => {
  const [r, g, b] = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** 400 → "400", 12345 → "12,345". Утга байхгүй бол зурааc. */
const formatCount = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString("en-US") : "-";

/** Анхны харагдац — "Сүүлийн 30 хоног" preset-тэй яг таарна */
const defaultRange = () => [
  dayjs().subtract(29, "days").startOf("day"),
  dayjs().endOf("day"),
];

const StatsItem = ({ item }) => {
  const { title, value, percent, upOrDown } = item;
  const { color, Icon } = STATS_STYLE[title] ?? FALLBACK_STATS_STYLE;
  const isUp = upOrDown === "up";
  // Backend хувь явуулаагүй үед хуурамч "0%" харуулахгүй, мөрийг бүрэн нууна
  const hasGrowth = Number.isFinite(Number(percent));

  return (
    <div className="sts_item_container">
      <div className="sts_icon" style={{ backgroundColor: withAlpha(color, 0.12) }}>
        <Icon size={22} style={{ color }} />
      </div>
      <div className="sts_content_wrapper">
        <div className="sts_header">{title ?? "-"}</div>
        <div className="sts_value">{formatCount(value)}</div>
        {hasGrowth && (
          <div className="sts_percent_row">
            <span className="sts_percent_label">Өмнөх үеэс</span>
            <span
              className="sts_percent_container"
              style={{ color: isUp ? GROWTH_UP_COLOR : GROWTH_DOWN_COLOR }}
            >
              {isUp ? <CaretUpOutlined /> : <CaretDownOutlined />}
              <span className="sts_percent">{`${percent}%`}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const StatsSkeleton = () => (
  <div className="sts_item_container">
    <Skeleton
      active
      avatar={{ shape: "circle", size: 44 }}
      title={false}
      paragraph={{ rows: 2, width: ["55%", "75%"] }}
    />
  </div>
);

const Dashboard = () => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [range, setRange] = useState(defaultRange);

  /**
   * Огноог хурдан хурдан сольход хариунууд эрэмбээ алдаж ирж болзошгүй тул
   * зөвхөн хамгийн сүүлийн хүсэлтийн хариуг л дэлгэцэнд буулгана.
   */
  const latestRequest = useRef(0);

  const getDashboard = useCallback(async () => {
    const [startDate, endDate] = range;
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    setLoading(true);
    setFailed(false);
    try {
      const result = await axios.post("/api/v0.1/merchant/dashboard?raw=true", {
        startDate,
        endDate,
      });
      if (requestId !== latestRequest.current) return;
      setData(result?.data?.result);
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      // Алдааны toast-ыг App.js доторх axios interceptor аль хэдийн гаргасан
      // тул энд зөвхөн "Дахин оролдох" боломжтой байдалд шилжинэ
      setFailed(true);
      setData(undefined);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    getDashboard();
  }, [getDashboard]);

  const changeDate = (value) => {
    // Цэвэрлэхэд `null` ирдэг — анхны 30 хоног руу буцаана
    if (!value?.[0] || !value?.[1]) {
      setRange(defaultRange());
      return;
    }
    setRange([value[0].startOf("day"), value[1].endOf("day")]);
  };

  const presets = useMemo(
    () => [
      { label: "Өнөөдөр", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
      {
        label: "Сүүлийн 7 хоног",
        value: [dayjs().subtract(6, "days").startOf("day"), dayjs().endOf("day")],
      },
      {
        label: "Сүүлийн 30 хоног",
        value: [dayjs().subtract(29, "days").startOf("day"), dayjs().endOf("day")],
      },
      {
        label: "Энэ сар",
        value: [dayjs().startOf("month"), dayjs().endOf("day")],
      },
      {
        label: "Өнгөрсөн сар",
        value: [
          dayjs().subtract(1, "month").startOf("month"),
          dayjs().subtract(1, "month").endOf("month"),
        ],
      },
    ],
    []
  );

  /** Графикт хэрэгтэй гурван цуваа — datа өөрчлөгдөх үед л дахин бодогдоно */
  const dailyChart = useMemo(() => {
    const rows = data?.deliveredChartData ?? [];
    return {
      saleDate: rows.map(({ _id }) => _id),
      salesAmount: rows.map(({ totalValue }) => totalValue),
      salesUnit: rows.map(({ totalUnit }) => totalUnit),
    };
  }, [data]);

  const calendarPicker = (
    <div className="db_dt_picker">
      <RangePicker
        placement="bottomRight"
        className="range_picker_style"
        value={range}
        presets={presets}
        format={DATE_FORMAT}
        // Ирээдүйн огноогоор мэдээлэл байхгүй тул сонгуулахгүй
        disabledDate={(current) => current && current > dayjs().endOf("day")}
        onChange={changeDate}
      />
    </div>
  );

  return (
    <div>
      <PPPageHeader title={"Хянах самбар"} extra={calendarPicker} />
      <div className="db_wrapper">
        {failed ? (
          <Alert
            type="error"
            showIcon
            message="Мэдээлэл татахад алдаа гарлаа"
            description="Сүлжээгээ шалгаад дахин оролдоно уу."
            action={
              <Button size="small" onClick={getDashboard}>
                Дахин оролдох
              </Button>
            }
          />
        ) : (
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <Row gutter={[16, 16]}>
                {loading
                  ? [0, 1, 2, 3].map((key) => (
                      <Col key={key} xs={24} sm={12} xl={6}>
                        <StatsSkeleton />
                      </Col>
                    ))
                  : data?.orderStats?.map((item) => (
                      <Col key={item.title} xs={24} sm={12} xl={6}>
                        <StatsItem item={item} />
                      </Col>
                    ))}
              </Row>
            </Col>
            <Col xs={24} xl={12}>
              <SalesChart loading={loading} dailyChart={dailyChart} />
            </Col>
            <Col xs={24} xl={12}>
              <PendingOrders loading={loading} ordersWithNote={data?.ordersWithNote} />
            </Col>
            {(loading || data?.products?.length > 0) && (
              <Col xs={24}>
                <Products loading={loading} products={data?.products} />
              </Col>
            )}
          </Row>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
